import PubSub from "pubsub-js";
import { isEmpty } from "underscore";
import { MetricConstants } from "../../components/Constants";
import { pubsubEvents } from "../../components/helpers/socket/pubsubEvent";
import config from "../../components/helpers/socket/config";
import optionsGridConfig from "../../platforms/cmed/OptionsGrid/optionsGridConfig";
import { OrderStatus } from "../../platforms/cmed/OrderBook/Constants";
import { endMetric } from "../metric-utils/metricUtils";

let updateBuffer = [];
let toastTimer = null;

const statusGetter = (orderStatus) => {
  switch (orderStatus) {
    case 0:
      return OrderStatus.HELD;
    case 1:
      return OrderStatus.WORKING;
    case 2:
      return OrderStatus.PARTIAL_FILLED;
    case 3:
      return OrderStatus.PARTIAL_FILLED_HELD;
    case 4:
      return OrderStatus.PARTIAL_FILLED_CANCELLED;
    case 5:
      return OrderStatus.FILLED;
    case 6:
      return OrderStatus.CANCELLED;
    case 7:
      return OrderStatus.PENDING;
    case 8:
      return OrderStatus.PENDING_HELD;
    default:
      return OrderStatus.HELD;
  }
};

const statusColorGetter = (orderStatus) => {
  switch (orderStatus) {
    case 0:
    case 3:
    case 8:
      return "medium";
    case 1:
    case 2:
      return "warning";
    case 7:
      return "dark";
    case 5:
      return "success";
    case 4:
    case 6:
      return "danger";
    default:
      return "medium";
  }
};

const processToastNotifications = () => {
  const data = Array.from(
    new Map(updateBuffer.map((item) => [item.id, item])).values(),
  );
  updateBuffer = [];
  toastTimer = null;

  if (!data.length) return;
  const isAllCancelled = data.every(
    (item) =>
      statusGetter(item.s) === OrderStatus.CANCELLED ||
      statusGetter(item.s) === OrderStatus.PARTIAL_FILLED_CANCELLED,
  );

  const isAllHeld = data.every(
    (item) =>
      statusGetter(item.s) === OrderStatus.HELD ||
      statusGetter(item.s) === OrderStatus.PARTIAL_FILLED_HELD ||
      statusGetter(item.s) === OrderStatus.PENDING_HELD,
  );

  if (data.length > 1 && isAllCancelled) {
    const message = `${data.length} orders cancelled`;
    const color = statusColorGetter(6); // CANCELLED
    PubSub.publish(pubsubEvents.successKeys.TRADING_SERVICE_TOAST_TOP, [
      message,
      color,
    ]);
  } else if (data.length > 1 && isAllHeld) {
    const message = `${data.length} orders held`;
    const color = statusColorGetter(0); // HELD
    PubSub.publish(pubsubEvents.successKeys.TRADING_SERVICE_TOAST_TOP, [
      message,
      color,
    ]);
  } else {
    data.forEach((contractDetails) => {
      const { p: contractName, d: contractDate, fq, q, s } = contractDetails;
      const status = statusGetter(s);
      const fillStatus = `${fq?.f ?? 0}/${q?.f ?? 0}`;
      const message = `Order for ${contractName} ${contractDate} ${status} with fill status ${fillStatus}`;
      const color = statusColorGetter(s);
      PubSub.publish(pubsubEvents.successKeys.TRADING_SERVICE_TOAST_TOP, [
        message,
        color,
      ]);
    });
  }
};

export function cmed_order_book_messageReceived(data) {
  const { payload } = data;

  if (data.status && (data.status === "ERROR" || data.status === "FAILURE")) {
    if (isEmpty(data.warnings)) {
      PubSub.publish(pubsubEvents.socketKeys.HANDLE_SOCKET_ERROR, data);
    }
    console.log(`❌ ${data.errors}`);
    if (
      data.errors[0] !== pubsubEvents.errorMessage.REQUEST_ERROR_CMED_TIMEOUT &&
      data.errors[0] !==
        pubsubEvents.errorMessage.CMED_TOKEN_UNKNOWN_SQUARE_BRACKETS &&
      data.errors[0] !== pubsubEvents.errorMessage.CMED_TOKEN_UNKNOWN &&
      data.errors[0] !== pubsubEvents.errorMessage.ERROR_MESSAGE_GENERIC
    ) {
      switch (data.action) {
        case config.socket.actions.CMED_ORDERS_SUBSCRIBE:
          console.log(`❌ ${data.type}`);
          PubSub.publish(pubsubEvents.errorKeys.MARKET_SERVICE_ERROR_TOAST, {
            errors: "There was an unexpected error while fetching data.",
          });
          break;
        case config.socket.actions.CMED_ORDER_ACTIVATE:
          endMetric(MetricConstants.ORDER_ACTIVATE_LATENCY);
          console.log(`❌ ${data.type}`);
          PubSub.publish(
            pubsubEvents.errorKeys.MARKET_SERVICE_ERROR_TOAST,
            data,
          );
          break;
        case config.socket.actions.CMED_ORDER_CANCEL:
          console.log(`❌ ${data.type}`);
          endMetric(MetricConstants.ORDER_KILL_LATENCY);
          PubSub.publish(
            pubsubEvents.errorKeys.MARKET_SERVICE_ERROR_TOAST,
            data,
          );
          break;
        case config.socket.actions.CMED_ORDER_HOLD:
          console.log(`❌ ${data.type}`);
          endMetric(MetricConstants.ORDER_HOLD_LATENCY);
          PubSub.publish(
            pubsubEvents.errorKeys.MARKET_SERVICE_ERROR_TOAST,
            data,
          );
          break;
        case config.socket.actions.CMED_ORDER_TICKUP:
          console.log(`❌ ${data.type}`);
          PubSub.publish(
            pubsubEvents.errorKeys.MARKET_SERVICE_ERROR_TOAST,
            data,
          );
          break;
        case config.socket.actions.CMED_ORDER_TICKDOWN:
          console.log(`❌ ${data.type}`);
          PubSub.publish(
            pubsubEvents.errorKeys.MARKET_SERVICE_ERROR_TOAST,
            data,
          );
          break;
        case config.socket.actions.CMED_ORDER_HOLDALL:
          console.log(`❌ ${data.type}`);
          endMetric(MetricConstants.ORDER_HOLD_ALL_LATENCY);
          PubSub.publish(pubsubEvents.errorKeys.MARKET_SERVICE_ERROR_TOAST, {
            errors: "There was an unexpected error while processing hold.",
          });
          break;
        case config.socket.actions.CMED_ORDER_CANCELALL:
          console.log(`❌ ${data.type}`);
          endMetric(MetricConstants.ORDER_KILL_ALL_LATENCY);
          PubSub.publish(pubsubEvents.errorKeys.MARKET_SERVICE_ERROR_TOAST, {
            errors:
              "There was an unexpected error while processing cancellation.",
          });
          break;
        case config.socket.actions.CMED_ORDER_AMEND:
          console.log(`❌ ${data.type}`);
          endMetric(MetricConstants.ORDER_MODIFY_LATENCY);
          PubSub.publish(pubsubEvents.errorKeys.MARKET_SERVICE_ERROR_TOAST, {
            errors: [
              data?.errors?.length > 0
                ? data?.errors[0]
                : data?.payload?.message,
            ],
          });
          PubSub.publish(
            optionsGridConfig.socketKeys.CMED_ORDER_AMENDMENT_ERROR,
            {
              errors: [
                data?.errors?.length > 0
                  ? data?.errors[0]
                  : data?.payload?.message,
              ],
            },
          );
          break;
        default:
          console.log(`🔴 No matching error action found for ${data.type}`);
          break;
      }
    }
  } else if (payload !== null) {
    switch (payload.subscriptionKind) {
      case "TradingInstrumentsForUser":
        console.log(`✅ ${payload.subscriptionKind}`);
        PubSub.publish(
          pubsubEvents.stompSubscriptionKeys.CMED_ORDER_INSTRUMENTS_UPDATE,
          data,
        );
        break;
      case "TradingOrders":
        console.log(`✅ ${payload.subscriptionKind}`);

        if (payload?.data && !Array.isArray(payload.data)) {
          payload.data = [payload.data];
        }

        PubSub.publish(
          pubsubEvents.stompSubscriptionKeys.CMED_ORDER_UPDATES,
          data,
        );
        if (payload?.isSnapshot === null) {
          const incomingData = payload?.data || [];
          if (!incomingData.length) return;

          updateBuffer.push(...incomingData);

          if (toastTimer) clearTimeout(toastTimer);

          toastTimer = setTimeout(processToastNotifications, 1500);
        }
        break;
      case "TradingOrderDetails":
        console.log(`✅ ${payload.subscriptionKind}`);
        PubSub.publish(
          pubsubEvents.stompSubscriptionKeys.CMED_ORDER_DETAILS_UPDATE,
          data,
        );
        break;
      case "ContractMarketData":
        console.log(`✅ ${payload.subscriptionKind}`);
        PubSub.publish(
          pubsubEvents.stompSubscriptionKeys.CONTRACT_MARKET_DATA,
          data,
        );
        break;
      case "TradingOrderFills":
        console.log(`✅ ${payload.subscriptionKind}`);
        PubSub.publish(
          pubsubEvents.stompSubscriptionKeys.CMED_ORDER_FILLS_UPDATE,
          data,
        );
        break;
      case "TradingOrderMarketData":
        console.log(`✅ ${payload.subscriptionKind}`);
        PubSub.publish(
          pubsubEvents.stompSubscriptionKeys.CMED_ORDER_MARKET_DATA_UPDATE,
          data,
        );
        break;
      case "TradingOrderBidOffers":
        console.log(`✅ ${payload.subscriptionKind}`);
        PubSub.publish(
          pubsubEvents.stompSubscriptionKeys.CMED_ORDER_BID_OFFERS_UPDATE,
          data,
        );
        break;
      case "TradingPositionAggregates":
        console.log(`✅ ${payload.subscriptionKind}`);
        PubSub.publish(
          pubsubEvents.stompSubscriptionKeys.CMED_POSITION_AGGREGATES_UPDATE,
          data,
        );
        break;
      default:
        console.log(
          `🔴 No matching action found for ${payload.subscriptionKind}`,
        );
        break;
    }

    switch (payload?.action) {
      case "OrderAmend":
        endMetric(MetricConstants.ORDER_MODIFY_LATENCY);
        PubSub.publish(optionsGridConfig.socketKeys.CMED_ORDER_AMENDMENT, data);
        break;
      default:
        console.log(
          `🔴 No matching action found for ${payload.subscriptionKind}`,
        );
        break;
    }

    if (payload) {
      if (
        data.status &&
        (data.status === "ERROR" || data.status === "FAILURE") &&
        payload.action !== "Activate"
      ) {
        console.log(`✅ ${payload.action}`);
        PubSub.publish(pubsubEvents.eventKeys.DISPOSE_ANY_CARD_OVERLAYS, data);
        PubSub.publish(pubsubEvents.socketKeys.HANDLE_SOCKET_ERROR, data);
      } else if (payload.action === "Activate") {
        console.log(`✅ ${payload.action}`);
        endMetric(MetricConstants.ORDER_ACTIVATE_LATENCY);
        PubSub.publish(
          pubsubEvents.stompSubscriptionKeys.CMED_ORDER_ACTION_ACTIVATE,
          data,
          { status: data.status },
        );
      } else if (payload.action === "Hold") {
        console.log(`✅ ${payload.action}`);
        endMetric(MetricConstants.ORDER_HOLD_LATENCY);
      } else if (payload.action === "Kill") {
        console.log(`✅ ${payload.action}`);
        endMetric(MetricConstants.ORDER_KILL_LATENCY);
      } else if (payload.action === "HoldAll") {
        console.log(`✅ ${payload.action}`);
        endMetric(MetricConstants.ORDER_HOLD_ALL_LATENCY);
        PubSub.publish(pubsubEvents.eventKeys.DISPOSE_ANY_CARD_OVERLAYS, data);
        PubSub.publish(pubsubEvents.eventKeys.BULK_ACTION_COMPLETE);
      } else if (payload.action === "KillAll") {
        console.log(`✅ ${payload.action}`);
        endMetric(MetricConstants.ORDER_KILL_ALL_LATENCY);
        PubSub.publish(pubsubEvents.eventKeys.DISPOSE_ANY_CARD_OVERLAYS, data);
        PubSub.publish(pubsubEvents.eventKeys.BULK_ACTION_COMPLETE);
      }
    } else {
      console.log("subscription.error", data.errors);
    }
  }
  console.groupEnd();
}
